import useAsync from 'react-use/lib/useAsync';
import FieldService, { isFieldAvailable } from '../services/FieldService';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import {
  getBusinessMergeFields,
  getTeamMemberMergeFields,
} from 'components/WYSIWYG/use-merge-fields';
import { isMergeFieldUnescaped } from 'utility/merge-fields';
import { v4 as uuidv4 } from 'uuid';
import {
  getAutomationVariablesCategoryName,
  baseAutomationVariable,
} from './mergeVariableHelpers';

export const useMergeFieldsBase = ({
  fieldsFor,
  mergeFieldName,
  teamMemberCategoryName,
  linkToRecord,
  automationVariables = [],
  isGlobalAutomation = false,
}) => {
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);

  const { loading, value: categorizedFieldOptions = [] } =
    useAsync(async () => {
      const categorizedFields = isGlobalAutomation
        ? []
        : await FieldService.getCategorizedFields(fieldsFor);

      const mergeOptions = categorizedFields.map(({ name, fields, id }) => {
        const fieldsToDisplay = fields.filter(isFieldAvailable);

        return {
          label: name,
          key: id,
          items: fieldsToDisplay.map((field) => {
            return {
              label: field.displayName,
              relationship: `${mergeFieldName}.${field.name}`,
              unescaped: isMergeFieldUnescaped(field),
              key: field.id,
            };
          }),
        };
      });

      if (!isGlobalAutomation && linkToRecord) {
        if (mergeOptions?.length) {
          mergeOptions[0].items = [
            linkToRecord,
            ...(mergeOptions[0]?.items ?? []),
          ];
        } else {
          mergeOptions.push({
            label: t('System Fields'),
            key: uuidv4(),
            items: [linkToRecord],
          });
        }
      }

      return mergeOptions;
    }, []);

  let mergeOptions = categorizedFieldOptions;

  if (automationVariables.length) {
    mergeOptions = [
      {
        key: uuidv4(),
        label: getAutomationVariablesCategoryName(t),
        items: automationVariables.map(({ label, value }) =>
          baseAutomationVariable(label, value)
        ),
      },
      ...mergeOptions,
    ];
  }

  return {
    categorizedFieldOptions: mergeOptions.concat(
      {
        label: teamMemberCategoryName || t('Team Member'),
        key: 'team_member_group',
        items: getTeamMemberMergeFields(t),
      },
      {
        key: 'business_fields_group',
        label: t('Business Fields'),
        items: getBusinessMergeFields(t, chosenBusiness.business_variables),
      }
    ),
    loading,
  };
};

export const useContactsMergeFields = () => {
  return useMergeFieldsBase({
    fieldsFor: { for: 'contacts' },
    mergeFieldName: 'contact',
  });
};
